import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Loader2, ScanBarcode, DoorOpen, PackageCheck, Factory,
  Palette, ClipboardCheck, ListChecks, Pin, PenLine, Package, ArrowRightLeft,
  Warehouse, MapPin, Hand, User, Clock, Hash, AlertCircle, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const fmtIST = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
  } catch { return String(iso); }
};

const StaffTime = ({ staff, name, time }) => (
  <div className="flex flex-col gap-1 text-sm">
    <span className="flex items-center gap-1.5 text-gray-800">
      <User className="w-3.5 h-3.5 text-gray-400" />
      {name || staff?.staff_name || <span className="text-gray-400">Unknown staff</span>}
      {(staff?.staff_id) && <span className="text-xs text-gray-400">({staff.staff_id})</span>}
    </span>
    <span className="flex items-center gap-1.5 text-gray-500">
      <Clock className="w-3.5 h-3.5 text-gray-400" />
      {fmtIST(time) || <span className="text-gray-400">—</span>}
    </span>
  </div>
);

const BundleTracker = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (q.length < 3) {
      toast.warning('Scan or enter a bundle barcode (min 3 characters)');
      return;
    }
    setLoading(true);
    setData(null);
    setNotFound(false);
    try {
      const res = await apiClient.get('/greige-storage/bundle-tracker', {
        params: { bundle_barcode: q },
      });
      if (!res.data?.found) {
        setNotFound(true);
      } else {
        setData(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load bundle tracker');
    } finally {
      setLoading(false);
    }
  };

  const s = data?.sections || {};

  // Build the ordered stage list (1..16) once data is present.
  const stages = data ? [
    { n: 1, icon: DoorOpen, title: 'Gate Entry',
      present: s.gate_entry?.present,
      body: <span className="flex items-center gap-1.5 text-sm text-gray-600">
        <Clock className="w-3.5 h-3.5 text-gray-400" />
        {fmtIST(s.gate_entry?.timestamp_ist) || '—'}
      </span>,
      extra: s.gate_entry?.entry_id },
    { n: 2, icon: PackageCheck, title: 'Greige Section Bundle Receipt',
      present: s.greige_receipt?.present,
      body: <StaffTime name={s.greige_receipt?.received_by} staff={null} time={s.greige_receipt?.received_at_ist} /> },
    { n: 3, icon: Factory, title: 'Vendor Name',
      present: !!s.vendor_name,
      body: <span className="text-sm font-medium text-gray-800">{s.vendor_name || '—'}</span> },
    { n: 4, icon: Hash, title: 'Sort No',
      present: !!s.sort_no,
      body: <div className="space-y-0.5 text-sm">
        <div className="font-medium text-gray-800">{s.sort_no || '—'}</div>
        <div className="text-gray-500">
          Size: <span className="text-gray-700">{s.size || '—'}</span>
          {' · '}Design: <span className="text-gray-700">{s.item?.design_name || '—'}</span>
          {' · '}Cat2: <span className="text-gray-700">{s.item?.category2 || '—'}</span>
        </div>
      </div> },
    { n: 5, icon: Palette, title: 'Color',
      present: !!(s.color?.color_name || s.color?.color_code),
      body: <span className="text-sm font-medium text-gray-800">
        {s.color?.color_name || '—'}{s.color?.color_code ? ` (${s.color.color_code})` : ''}
      </span> },
    { n: 6, icon: ClipboardCheck, title: 'Sort No Assignment',
      present: s.sort_no_assignment?.present,
      body: <StaffTime staff={s.sort_no_assignment?.staff} time={s.sort_no_assignment?.timestamp_ist} /> },
    { n: 7, icon: ListChecks, title: 'Quantity',
      present: (s.quantity ?? 0) > 0,
      body: <span className="text-sm font-semibold text-gray-800">{s.quantity ?? 0} pcs</span> },
    { n: 8, icon: Search, title: 'Inspection',
      present: s.inspection?.present,
      body: <StaffTime staff={s.inspection?.staff} time={s.inspection?.timestamp_ist} /> },
    { n: 9, icon: Pin, title: 'Pinning',
      present: s.pinning?.present,
      body: <StaffTime staff={s.pinning?.staff} time={s.pinning?.timestamp_ist} /> },
    { n: 10, icon: PenLine, title: 'Marker Writing',
      present: s.marker_writing?.present,
      body: <StaffTime staff={s.marker_writing?.staff} time={s.marker_writing?.timestamp_ist} /> },
    { n: 11, icon: Package, title: 'Packing',
      present: s.packing?.present,
      body: <StaffTime staff={s.packing?.staff} time={s.packing?.timestamp_ist} /> },
    { n: 12, icon: ArrowRightLeft, title: 'Greige Storage Transfer',
      present: s.greige_storage_transfer?.present,
      body: <StaffTime name={s.greige_storage_transfer?.created_by_name} time={s.greige_storage_transfer?.created_at_ist} /> },
    { n: 13, icon: Warehouse, title: 'Greige Storage Receipt',
      present: s.greige_storage_receipt?.present,
      body: <StaffTime name={s.greige_storage_receipt?.completed_by} time={s.greige_storage_receipt?.completed_at_ist} />,
      status: s.greige_storage_receipt?.status },
    { n: 14, icon: MapPin, title: 'Greige Storage Location',
      present: s.greige_storage_location?.present,
      body: <div className="space-y-1">
        <div className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-gray-400" />
          {s.greige_storage_location?.footprint || '—'}
        </div>
        <StaffTime staff={s.greige_storage_location?.staff} time={s.greige_storage_location?.put_away_at_ist} />
      </div>,
      status: s.greige_storage_location?.status },
    { n: 15, icon: RefreshCw, title: 'Sort No Transfer',
      present: s.sort_no_transfer?.present,
      body: <div className="space-y-1">
        <div className="text-sm text-gray-800">
          <span className="text-gray-500">{s.sort_no_transfer?.old_sort_no || '—'}</span>
          {' → '}
          <span className="font-semibold text-gray-900">{s.sort_no_transfer?.new_sort_no || '—'}</span>
        </div>
        <div className="text-xs text-gray-500">
          New Size: <span className="text-gray-700">{s.sort_no_transfer?.new_sort_no_details?.size || '—'}</span>
          {' · '}Design: <span className="text-gray-700">{s.sort_no_transfer?.new_sort_no_details?.design_name || '—'}</span>
          {' · '}Cat2: <span className="text-gray-700">{s.sort_no_transfer?.new_sort_no_details?.category2 || '—'}</span>
        </div>
        <StaffTime staff={s.sort_no_transfer?.staff} time={s.sort_no_transfer?.transferred_at_ist} />
      </div> },
    { n: 16, icon: Hand, title: 'Greige Storage Pick Slip',
      present: s.greige_storage_pick_slip?.present,
      body: <StaffTime name={s.greige_storage_pick_slip?.picked_by} time={s.greige_storage_pick_slip?.picked_at_ist} />,
      extra: s.greige_storage_pick_slip?.pick_slip_id, status: s.greige_storage_pick_slip?.status },
    { n: 17, icon: ArrowRightLeft, title: 'Processing Transfer',
      present: s.processing_transfer?.present,
      body: <StaffTime name={s.processing_transfer?.created_by_name} time={s.processing_transfer?.created_at_ist} /> },
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige-storage')}
            data-testid="bundle-tracker-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-emerald-600 text-white"><ScanBarcode className="w-5 h-5" /></div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Bundle Tracker</h1>
              <p className="text-sm text-gray-500">Greige Storage · Scan a bundle for its full lifecycle</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-6">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Scan or type bundle number (e.g. BUN123456/26-27)"
            className="text-base"
            data-testid="bundle-tracker-input"
          />
          <Button type="submit" disabled={loading} data-testid="bundle-tracker-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2 hidden sm:inline">Track</span>
          </Button>
        </form>

        {notFound && (
          <Card data-testid="bundle-tracker-notfound">
            <CardContent className="py-10 text-center">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">No records found for “{query.trim()}”</p>
              <p className="text-sm text-gray-500 mt-1">Check the bundle number and try again.</p>
            </CardContent>
          </Card>
        )}

        {data && (
          <div data-testid="bundle-tracker-result">
            {/* Summary */}
            <Card className="mb-5 border-emerald-200">
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Bundle</p>
                    <p className="text-lg font-bold text-gray-900" data-testid="tracker-bundle-code">{data.bundle_barcode}</p>
                  </div>
                  <div><p className="text-xs text-gray-400 uppercase tracking-wide">Vendor</p><p className="font-medium text-gray-800">{s.vendor_name || '—'}</p></div>
                  <div><p className="text-xs text-gray-400 uppercase tracking-wide">Sort No</p><p className="font-medium text-gray-800">{s.sort_no || '—'}</p></div>
                  <div><p className="text-xs text-gray-400 uppercase tracking-wide">Size</p><p className="font-medium text-gray-800">{s.size || '—'}</p></div>
                  <div><p className="text-xs text-gray-400 uppercase tracking-wide">Design</p><p className="font-medium text-gray-800">{s.item?.design_name || '—'}</p></div>
                  <div><p className="text-xs text-gray-400 uppercase tracking-wide">Category 2</p><p className="font-medium text-gray-800">{s.item?.category2 || '—'}</p></div>
                  <div><p className="text-xs text-gray-400 uppercase tracking-wide">Color</p><p className="font-medium text-gray-800">{s.color?.color_name || '—'}</p></div>
                  <div><p className="text-xs text-gray-400 uppercase tracking-wide">Quantity</p><p className="font-semibold text-emerald-700">{s.quantity ?? 0} pcs</p></div>
                </div>
              </CardContent>
            </Card>

            {/* Timeline of 16 stages */}
            <div className="space-y-3">
              {stages.map((st) => {
                const Icon = st.icon;
                return (
                  <Card key={st.n} className={st.present ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-gray-200 opacity-70'}
                    data-testid={`tracker-stage-${st.n}`}>
                    <CardContent className="py-3.5">
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${st.present ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                          {st.n}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Icon className={`w-4 h-4 ${st.present ? 'text-emerald-600' : 'text-gray-400'}`} />
                            <span className="font-semibold text-gray-900 text-sm">{st.title}</span>
                            {st.status && <Badge variant="secondary" className="text-[10px] capitalize">{st.status}</Badge>}
                            {st.extra && <span className="text-[11px] text-gray-400">{st.extra}</span>}
                          </div>
                          {st.present ? st.body : <span className="text-sm text-gray-400">Not yet reached / no data</span>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BundleTracker;
